//node-modules
import { createContext, useContext, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { BrowserRouter } from "react-router-dom";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

//src
import "./index.css";

import UserRouter from "./ts/router/UserRouter";
import axios from "axios";
import { config } from "./ts/config/config";

// material UI
import { CircularProgress, createTheme, ThemeProvider } from "@mui/material";
import { Common } from "./common/src";
import { ProjectUserList } from "./common/src/modules";

declare global {
  interface Window {
    dataLayer: Array<Object>;
  }
}

declare module "@mui/material/styles" {
  interface Palette {
    base: {
      main: string;
      contrast: string;
      sub: string;
      modal: string;
      dialogBackground: string;
      card: string;
      switch: string;
      border: string;
      button: string;
      menu: string;
      ymdBackground: string;
      opacityMain: string;
      calcText: string;
      label?: string;
    };
    rtk: {
      main: string;
      dark: string;
      light: string;
    };
    gps: {
      main: string;
      dark: string;
      light: string;
    };
    btn: {
      default: {
        backgroundColor: string;
        color: string;
        border: string;
        hover: {
          backgroundColor: string;
          border: string;
          color: string;
        };
        disabled: {
          backgroundColor: string;
          border: string;
          color: string;
        };
      };
      icon: {
        backgroundColor: string;
        color: string;
        border: string;
        hover: {
          backgroundColor: string;
          border: string;
          color: string;
        };
        disabled: {
          backgroundColor: string;
          border: string;
          color: string;
        };
      };
      error: {
        backgroundColor: string;
        border: string;
        color: string;
        hover: {
          backgroundColor: string;
          border: string;
          color: string;
        };
        disabled: {
          backgroundColor: string;
          border: string;
          color: string;
        };
      };
      warning: {
        backgroundColor: string;
        border: string;
        color: string;
        hover: {
          backgroundColor: string;
          border: string;
          color: string;
        };
        disabled: {
          backgroundColor: string;
          border: string;
          color: string;
        };
      };
    };
  }
  interface PaletteOptions {
    base?: {
      main?: string;
      contrast?: string;
      sub?: string;
      modal?: string;
      dialogBackground: string;
      card?: string;
      switch?: string;
      border?: string;
      button?: string;
      menu?: string;
      ymdBackground?: string;
      opacityMain?: string;
      calcText?: string;
      label?: string;
    };
    rtk: {
      main: string;
      dark: string;
      light: string;
    };
    gps: {
      main: string;
      dark: string;
      light: string;
    };
    btn: {
      default: {
        backgroundColor: string;
        color: string;
        border: string;
        hover: {
          backgroundColor: string;
          border: string;
          color: string;
        };
        disabled: {
          backgroundColor: string;
          border: string;
          color: string;
        };
      };
      icon: {
        backgroundColor: string;
        color: string;
        border: string;
        hover: {
          backgroundColor: string;
          border: string;
          color: string;
        };
        disabled: {
          backgroundColor: string;
          border: string;
          color: string;
        };
      };
      error: {
        backgroundColor: string;
        border: string;
        color: string;
        hover: {
          backgroundColor: string;
          border: string;
          color: string;
        };
        disabled: {
          backgroundColor: string;
          border: string;
          color: string;
        };
      };
      warning: {
        backgroundColor: string;
        border: string;
        color: string;
        hover: {
          backgroundColor: string;
          border: string;
          color: string;
        };
        disabled: {
          backgroundColor: string;
          border: string;
          color: string;
        };
      };
    };
  }
}

// context
interface ThemeContextType {
  themeMode: string;
  setThemeMode: React.Dispatch<React.SetStateAction<string>>;
}

export const ThemeModeContext = createContext<ThemeContextType | undefined>(
  undefined
);

interface UserSiteContextType {
  userInfo: Common.LandlogNecessaryUserInfo;
  userList: ProjectUserList[];
  jobsiteList: Common.LandlogJobSite[];
}

export const UserSiteContext = createContext<UserSiteContextType | undefined>(
  undefined
);

export const useThemeMode = () => {
  const context = useContext(ThemeModeContext);
  if (!context) {
    throw new Error("useThemeMode must be used within a ThemeModeProvider");
  }
  return context;
};

const mainContent = document.getElementById("main-content");

// APIからmodeの値を取得
export const fetchMode = async () => {
  try {
    let settingAbortController = new AbortController();
    const apiUrl = `${config.serverHost}/api/v1/user/setting`;

    settingAbortController.abort();
    settingAbortController = new AbortController();

    const response = await axios.get(apiUrl, {
      withCredentials: true,
      signal: settingAbortController.signal,
    });

    return response.data.colorTheme;
  } catch (e) {
    console.error(`Error: ${e}`);
  }
};

let currentMode: string;
const getTheme = async (themeMode: string) => {
  currentMode = themeMode ? themeMode : await fetchMode();
  const isDarkMode = currentMode ? currentMode === "dark" : true;

  return createTheme({
    palette: {
      mode: isDarkMode ? "dark" : "light",
      primary: {
        main: isDarkMode ? "#0064BA" : "#2F24E6",
        dark: "#0064BA",
        light: "#0064BA",
      },
      secondary: {
        main: isDarkMode ? "#FFFFFF" : "#000000",
        dark: "#0064BA",
        light: "#0064BA",
      },
      base: {
        main: isDarkMode ? "#000000" : "#FFFFFF",
        contrast: isDarkMode ? "#FFFFFF" : "#000000",
        sub: isDarkMode ? "#17171A" : "#ECEDEE",
        modal: isDarkMode ? "#0D0D10" : "#FFFFFF",
        dialogBackground: isDarkMode
          ? "rgba(255, 255, 255, 0.5)"
          : "rgba(0, 0, 0, 0.5)",
        card: isDarkMode ? "#121212" : "#FFFFFF",
        switch: isDarkMode ? "#e0e0e0" : "#ECEDEE",
        border: isDarkMode ? "rgba(255, 255, 255, 0.4)" : "rgba(0, 0, 0, 0.23)",
        button: isDarkMode ? "#1C1C1C" : "#ECEDEE",
        menu: isDarkMode ? "#565664" : "#FFFFFF",
        ymdBackground: isDarkMode ? "#007bff" : "#2F24E6",
        opacityMain: isDarkMode
          ? "rgba(0, 0, 0, 0.7)"
          : "rgba(255, 255, 255, 0.7)",
        calcText: isDarkMode ? "#00D4FF" : "#2F24E6",
        label: isDarkMode ? "#FFFFFF" : "#575757",
      },
      info: {
        main: "#000000",
        dark: "#000000",
        light: "#000000",
      },
      error: {
        main: "#E10009",
        dark: "#E10009",
        light: "#E10009",
      },
      rtk: {
        main: "#24E71C",
        dark: "#24E71C",
        light: "#24E71C",
      },
      gps: {
        main: "#37C5F1",
        dark: "#37C5F1",
        light: "#37C5F1",
      },
      btn: {
        default: {
          backgroundColor: isDarkMode ? "rgba(0, 100, 186)" : "",
          border: isDarkMode ? "1px solid #29292F" : "1px solid #ECEDEE",
          color: isDarkMode ? "rgba(255, 255, 255)" : "",
          hover: {
            backgroundColor: isDarkMode ? "rgba(0, 100, 186, 0.8)" : "",
            border: isDarkMode ? "1px solid rgba(0, 0, 0, 0)" : "",
            color: isDarkMode ? "rgba(255, 255, 255)" : "",
          },
          disabled: {
            backgroundColor: isDarkMode
              ? "rgba(255, 255, 255, 0.3)"
              : "rgba(0, 0, 0, 0)",
            border: isDarkMode
              ? "1px solid rgba(255, 255, 255, 0.12)"
              : "1px solid rgba(0, 0, 0, 0.3)",
            color: isDarkMode
              ? "rgba(255, 255, 255, 0.3)"
              : "rgba(0, 0, 0, 0.3)",
          },
        },
        icon: {
          backgroundColor: "rgba(0, 0, 0, 0)",
          color: isDarkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
          border: isDarkMode
            ? "1px solid rgba(255, 255, 255, 0.7)"
            : "1px solid rgba(0, 0, 0, 0.6)",
          hover: {
            backgroundColor: isDarkMode
              ? "rgba(255, 255, 255, 0.3)"
              : "rgba(0, 0, 0, 0.1)",
            border: isDarkMode ? "" : "",
            color: isDarkMode
              ? "rgba(255, 255, 255, 0.7)"
              : "rgba(0, 0, 0, 0.6)",
          },
          disabled: {
            backgroundColor: isDarkMode
              ? "rgba(255, 255, 255, 0.3)"
              : "rgba(0, 0, 0, 0)",
            border: isDarkMode
              ? "1px solid rgba(255, 255, 255, 0.12)"
              : "1px solid rgba(0, 0, 0, 0.3)",
            color: isDarkMode
              ? "rgba(255, 255, 255, 0.3)"
              : "rgba(0, 0, 0, 0.3)",
          },
        },
        error: {
          backgroundColor: "rgba(244, 67, 54)",
          border: "1px solid rgba(0, 0, 0, 0)",
          color: "#FFFFFF",
          hover: {
            backgroundColor: "rgba(244, 67, 54, 0.8)",
            border: "1px solid rgba(0, 0, 0, 0)",
            color: "rgba(255, 255, 255)",
          },
          disabled: {
            backgroundColor: isDarkMode
              ? "rgba(255, 255, 255, 0.3)"
              : "rgba(0, 0, 0, 0)",
            border: isDarkMode
              ? "1px solid rgba(255, 255, 255, 0.12)"
              : "1px solid rgba(0, 0, 0, 0.3)",
            color: isDarkMode
              ? "rgba(255, 255, 255, 0.3)"
              : "rgba(0, 0, 0, 0.3)",
          },
        },
        warning: {
          backgroundColor: "rgba(244, 67, 54, 0)",
          border: "1px solid rgba(244, 67, 54, 0.8)",
          color: "rgba(244, 67, 54)",
          hover: {
            backgroundColor: "rgba(0, 0, 0, 0.12)",
            border: "1px solid rgba(244, 67, 54)",
            color: "rgba(244, 67, 54)",
          },
          disabled: {
            backgroundColor: isDarkMode
              ? "rgba(255, 255, 255, 0.3)"
              : "rgba(0, 0, 0, 0)",
            border: isDarkMode
              ? "1px solid rgba(255, 255, 255, 0.12)"
              : "1px solid rgba(0, 0, 0, 0.3)",
            color: isDarkMode
              ? "rgba(255, 255, 255, 0.3)"
              : "rgba(0, 0, 0, 0.3)",
          },
        },
      },
    },
  });
};

const App = () => {
  const [themeMode, setThemeMode] = useState(null);
  const [theme, setTheme] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.onbeforeunload = function () {
      document.cookie =
        "login_auth=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const fetchedTheme = await getTheme(themeMode);
        setThemeMode(currentMode);
        setTheme(fetchedTheme);
      } catch (error) {
        // console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [themeMode]);

  return loading ? (
    <div>
      <CircularProgress
        sx={{ position: "absolute", left: "50%", top: "50%" }}
      />
    </div>
  ) : (
    <ThemeModeContext.Provider value={{ themeMode, setThemeMode }}>
      {theme !== null ? (
        <ThemeProvider theme={theme}>
          <BrowserRouter>
            <DndProvider backend={HTML5Backend}>
              <UserRouter />
            </DndProvider>
          </BrowserRouter>
        </ThemeProvider>
      ) : null}
    </ThemeModeContext.Provider>
  );
};

ReactDOM.render(<App />, mainContent);
